import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { markEmailSignupComplete } from "@/components/site/EmailSignup";
import { BarrierBadge } from "@/components/site/CommercialModule";
import { SmartImage } from "@/components/site/SmartImage";

const INTERESTS = [
  "Wellness & Recovery",
  "Golf Simulators",
  "Outdoor Living",
  "Home Power & Technology",
];

export const EmailCapture = () => {
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState([]);
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const startedAt = useRef(Date.now());

  const toggle = (i) =>
    setSelected((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please tick the consent box so we may email you.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/subscribe", {
        email,
        interests: selected,
        tags: ["General Newsletter"],
        consent: true,
        company,
        source: "homepage",
        elapsed_ms: Date.now() - startedAt.current,
      });
      markEmailSignupComplete();
      setDone(true);
      toast.success("Almost there — please check your inbox to confirm.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "That didn't go through. Please check the address and try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="email-capture-section"
      className="bg-charcoal text-white px-5 md:px-8 py-20 md:py-28"
    >
      <div className="max-w-[1280px] mx-auto grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-start">
        <div>
          <span className="eyebrow eyebrow-light">Stay in the loop</span>
          <h2 className="display text-[40px] md:text-[52px] leading-[1.06] mt-5">
            Planning a home upgrade?
          </h2>
          <p className="text-[17px] md:text-[18px] leading-[1.65] text-[#CBC8C2] mt-6 max-w-md">
            Tell us what you're working on and we'll send new decision guides and planning tools for
            those projects. No product blasts, no urgency, no coupons.
          </p>
        </div>

        {done ? (
          <div data-testid="email-capture-success" className="border border-white/15 px-8 py-10">
            <Check className="text-sage" size={26} />
            <h3 className="display text-[27px] mt-4">Thank you — you're on the list.</h3>
            <p className="text-[16px] text-[#CBC8C2] mt-3">
              We'll send new decision guides and planning tools for the projects you picked.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="email-capture-form">
            <div className="text-[11px] tracking-[0.18em] uppercase text-[#A9B8A2] font-semibold mb-4">
              What are you planning?
            </div>
            <div className="flex flex-wrap gap-2.5 mb-8">
              {INTERESTS.map((i) => {
                const on = selected.includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggle(i)}
                    data-testid={`interest-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`px-4 py-2.5 text-[13px] font-light border transition-colors duration-300 ${
                      on
                        ? "bg-sage border-sage text-white"
                        : "border-white/25 text-[#CFCCC7] hover:border-white/60"
                    }`}
                  >
                    {i}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <label htmlFor="subscribe-email" className="sr-only">
                Email address
              </label>
              <input
                id="subscribe-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                aria-label="Email address"
                data-testid="email-capture-input"
                className="flex-1 bg-transparent border border-white/25 px-5 py-4 text-[15px] font-light placeholder:text-[#9E9B95] focus:outline-none focus:border-sage transition-colors"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="email-capture-submit"
                className="px-8 py-4 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase hover:bg-white hover:text-charcoal transition-colors duration-300 disabled:opacity-60"
              >
                {busy ? "Saving…" : "Subscribe"}
              </button>
            </div>
            <div aria-hidden="true" className="absolute left-[-9999px] w-px h-px overflow-hidden">
              <label htmlFor="subscribe-company">Company</label>
              <input
                id="subscribe-company"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>
            <label
              htmlFor="subscribe-consent"
              className="flex items-start gap-2.5 mt-5 text-[13px] font-light text-[#CBC8C2] leading-relaxed cursor-pointer"
            >
              <input
                id="subscribe-consent"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="email-capture-consent"
                className="mt-[3px] h-4 w-4 shrink-0 accent-[#7a8b73]"
              />
              <span>
                Yes, email me occasional buying guides, product discoveries and home living
                inspiration.
              </span>
            </label>
            <p className="text-[13px] text-[#B6B3AD] mt-4">
              No product blasts and no coupons. Unsubscribe from any email we send.
            </p>
          </form>
        )}
      </div>
    </section>
  );
};

export const GuideCard = ({ a, index }) => (
  <Link
    to={`/guides/${a.slug}`}
    data-testid={`guide-card-${a.slug}`}
    className="group block bg-white border border-line hover:border-sage/50 transition-colors duration-500"
  >
    {a.image && (
      <div className="overflow-hidden aspect-[16/10]">
        <SmartImage
          src={a.image}
          alt={a.image_alt || a.title}
          sizes="(min-width:1024px) 33vw, 100vw"
          maxWidth={768}
          width="1600"
          height="1000"
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
        />
      </div>
    )}
    <div className="px-6 py-7">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <BarrierBadge barrier={a.purchase_barrier} testId={`guide-card-barrier-${a.slug}`} />
      </div>
      <h3 className="display text-[24px] md:text-[26px] leading-[1.2] text-charcoal group-hover:text-sage-dark transition-colors duration-300">
        {a.title}
      </h3>
      <p className="text-[16px] md:text-[17px] leading-[1.6] text-[#454545] mt-3.5 line-clamp-3">
        {a.excerpt}
      </p>
      <span className="inline-flex items-center gap-1.5 text-[12.5px] tracking-[0.14em] uppercase text-[#48563F] font-semibold mt-6">
        Read the guide <ArrowUpRight size={14} />
      </span>
    </div>
  </Link>
);

/**
 * An image-led decision card for the homepage question set. The photograph comes from the homepage
 * editorial mapping and falls back to the guide's own hero.
 */
export const QuestionCard = ({ a, index }) => (
  <Link
    to={`/guides/${a.slug}`}
    data-testid={`question-card-${a.slug}`}
    className="group bg-white flex flex-col hover:bg-[#FBFAF8] transition-colors duration-500"
  >
    <div className="relative aspect-[16/10] overflow-hidden">
      <SmartImage
        src={a.card_image || a.image}
        alt={a.card_image_alt || a.image_alt || a.target_question || a.title}
        sizes="(min-width:1024px) 50vw, 100vw"
        maxWidth={1024}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-1000 ease-out"
      />
    </div>
    <div className="px-7 md:px-9 py-8 md:py-10 flex flex-col flex-1">
      <div className="flex items-center gap-3">
        <span className="display text-[17px] text-sage/70">
          {String(index + 1).padStart(2, "0")}
        </span>
        <BarrierBadge barrier={a.purchase_barrier} testId={`question-barrier-${a.slug}`} />
      </div>
      <h3 className="display text-[25px] md:text-[30px] leading-[1.18] text-charcoal mt-5 group-hover:text-sage-dark transition-colors duration-300">
        {a.target_question || a.title}
      </h3>
      <span
        data-testid={`question-family-${a.slug}`}
        className="text-[15.5px] md:text-[16.5px] leading-snug text-[#454545] mt-3.5"
      >
        {a.product_family}
      </span>
      <span
        data-testid={`question-cta-${a.slug}`}
        className="mt-auto pt-8 inline-flex"
      >
        <span className="inline-flex items-center gap-2 px-6 py-3.5 bg-charcoal text-white text-[11.5px] tracking-[0.16em] uppercase font-semibold group-hover:bg-sage transition-colors duration-300">
          Read the guide <ArrowUpRight size={14} />
        </span>
      </span>
    </div>
  </Link>
);


export const QuestionRow = ({ a, index }) => (
  <Link
    to={`/guides/${a.slug}`}
    data-testid={`question-row-${a.slug}`}
    className="group grid md:grid-cols-[auto_1fr_auto] gap-4 md:gap-8 items-baseline py-7 border-b border-line hover:bg-white/60 transition-colors duration-400 px-1"
  >
    <span className="display text-[20px] text-sage/70 w-8">
      {String(index + 1).padStart(2, "0")}
    </span>
    <div>
      <h3 className="display text-[23px] md:text-[29px] leading-[1.2] text-charcoal group-hover:text-sage-dark transition-colors duration-300">
        {a.target_question || a.title}
      </h3>
      <div className="flex flex-wrap items-center gap-2.5 mt-3">
        <BarrierBadge barrier={a.purchase_barrier} testId={`question-barrier-${a.slug}`} />
        <span
          data-testid={`question-family-${a.slug}`}
          className="text-[15px] md:text-[16px] leading-snug text-[#454545]"
        >
          {a.product_family}
        </span>
      </div>
    </div>
    <ArrowUpRight
      size={20}
      className="text-[#B8B4AD] group-hover:text-sage group-hover:-translate-y-0.5 transition-all duration-300 hidden md:block"
    />
  </Link>
);
